import { Injectable } from '@angular/core';
import { ENV } from 'environments/environment';
import { CONSTANTS, SearchConfig } from 'environments/common';
import { HttpClient } from '@angular/common/http';
import { SessionService } from 'app/components/common/session/session.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UtilsService } from '../utils/utils.service';
import { transition } from '@angular/animations';

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  searchConfig: SearchConfig = {
    page: 0,
    size: 9,
    eventName: "",
    sapCode: "",
    eventCode: "",
    searchText: "",
    brand: "",
    selectedHierarchicalProperties: [],
    selectedProperties: [],
    selectedSectors: [],
    selectedHomeOds: [],
    selectedCountries: [],
    selectedMultiEvents: [],
    filter: "ONLY_EXHIBITORS",
    searchOrder: "BY_RELEVANCE",
    language: "es_ES",
    maxTextLength: 500,
    createCache: 0
  };

  selectedHierarchicalProperties: Array<any> = [];;
  selectedProperties: Array<any> = [];;
  selectedCountries: Array<any> = [];
  selectedMultiEvents: Array<any> = [];
  selectedSectors: Array<any> = [];
  selectedHomeOds: Array<any> = [];

  filterAnalytics: Set<string>;

  items: Array<any> = [];
  
  private itemsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public items$: Observable<any[]> = this.itemsSubject.asObservable();

  private numItemsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public numItems$: Observable<number> = this.numItemsSubject.asObservable();

  private resetItemsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public resetItems$: Observable<boolean> = this.resetItemsSubject.asObservable();


  constructor(private http: HttpClient, private sessionService: SessionService, public utilsService:UtilsService) {
    this.filterAnalytics = new Set();
    this.clearStoredSearchConfigs();
  }

  /**
   * searchConfig only lives while the app is loaded: every page load (F5, new, duplicated
   * or restored tab) starts clean. Legacy copies stored in localStorage by previous
   * versions are removed as well.
   */
  private clearStoredSearchConfigs(): void {
    this.removeSearchConfigKeys(localStorage);
    this.removeSearchConfigKeys(sessionStorage);
  }

  private removeSearchConfigKeys(storage: Storage): void {
    Object.keys(storage)
      .filter(key => key.startsWith('searchConfig_'))
      .forEach(key => storage.removeItem(key));
  }


  unifiedSearch(dataIn) {

    let dataPost = {
      "eventName": this.utilsService.isEmptyOrUndefined(dataIn.eventName) ? this.searchConfig.eventName : dataIn.eventName,
      "sapCode": this.utilsService.isEmptyOrUndefined(dataIn.sapCode) ? this.searchConfig.sapCode : dataIn.sapCode,
      "eventCode": this.utilsService.isEmptyOrUndefined(dataIn.eventCode) ? this.searchConfig.eventCode : dataIn.eventCode,
      "searchText": this.utilsService.isEmptyOrUndefined(dataIn.searchText) ? this.searchConfig.searchText : dataIn.searchText,
      "brand": this.utilsService.isEmptyOrUndefined(dataIn.brand) ? this.searchConfig.brand : dataIn.brand,
      "selectedHierarchicalProperties": this.utilsService.isEmptyOrUndefined(dataIn.selectedHierarchicalProperties) ? this.searchConfig.selectedHierarchicalProperties : dataIn.selectedHierarchicalProperties,
      "selectedProperties": this.utilsService.isEmptyOrUndefined(dataIn.selectedProperties) ? this.searchConfig.selectedProperties : dataIn.selectedProperties,
      "selectedSectors": this.utilsService.isEmptyOrUndefined(dataIn.selectedSectors) ? this.searchConfig.selectedSectors : dataIn.selectedSectors,
      "selectedHomeOds": this.utilsService.isEmptyOrUndefined(dataIn.selectedHomeOds) ? this.searchConfig.selectedHomeOds : dataIn.selectedHomeOds,
      "selectedCountries": this.utilsService.isEmptyOrUndefined(dataIn.selectedCountries) ? this.searchConfig.selectedCountries : dataIn.selectedCountries,
      "filter": this.utilsService.isEmptyOrUndefined(dataIn.filter) ? this.searchConfig.filter : dataIn.filter,
      "searchOrder": this.utilsService.isEmptyOrUndefined(dataIn.searchOrder) ? this.searchConfig.searchOrder : dataIn.searchOrder,
      "language": this.utilsService.isEmptyOrUndefined(dataIn.language) ? this.searchConfig.language : dataIn.language,
      "maxTextLength": this.utilsService.isEmptyOrUndefined(dataIn.maxTextLength) ? this.searchConfig.maxTextLength : dataIn.maxTextLength,
      "selectedMultiEvents": this.utilsService.isEmptyOrUndefined(dataIn.selectedMultiEvents) ? this.searchConfig.selectedMultiEvents : dataIn.selectedMultiEvents,
      "createCache": this.utilsService.isEmptyOrUndefined(dataIn.createCache) ? this.searchConfig.createCache : dataIn.createCache
    }

    let url = ENV.APP.USEARCH_API + CONSTANTS.END_POINTS.SEARCH.UNIFIED_SEARCH + '?page=' + dataIn.page + '&size=' + dataIn.size + '&language=' + dataIn.language;

    return this.http.post(url, dataPost);

  }

  getSearchConfig() {

    let eventData = JSON.parse(this.sessionService.get('eventData', false) || '{}');
    let searchConfig = JSON.parse(this.sessionService.get('searchConfig_' + eventData['sapCode'], false) || '{}');
  
    return this.utilsService.isEmptyObject(searchConfig)?this.searchConfig:searchConfig;
  }

  setSearchConfig(config: Partial<SearchConfig>): void {
    this.searchConfig = { ...this.searchConfig, ...config };
    this.saveSearchConfigInSession(this.searchConfig);
  }

  getSelectedProperty(propertyName) {
    if (this[propertyName]) {
      return this[propertyName];
    } else  {
      return [];
    }
  }

  addItemToProperty(propertyName, itemValue, item) {
    const propertyAsSet = new Set(this[propertyName]);
    propertyAsSet.add(itemValue);
    this[propertyName] = Array.from(propertyAsSet);
    this.searchConfig[propertyName] = this[propertyName];

    this.addFilterAnalytics(item);

    /** Save searchConfig on session */
    this.saveSearchConfigInSession(this.searchConfig);
  }

  removeItemFromProperty(propertyName, itemValue, item) {

    let index = this[propertyName].indexOf(itemValue);

    if (index != -1) {
      this[propertyName].splice(index, 1);
    }

    this.removeFilterAnalytics(item);

    this.searchConfig[propertyName] = this[propertyName];
    /** Save searchConfig on session */
    this.saveSearchConfigInSession(this.searchConfig);

  }

  addFilterAnalytics(item): void {
    let auxItem = {id: item.id, name: item.name ? item.name : item.label, translations: item.translations};
    const key = JSON.stringify(auxItem);
    if (!this.filterAnalytics.has(key)) {
      this.filterAnalytics.add(key);
    }
  }
  
  removeFilterAnalytics(item): void {
    let auxItem = {id: item.id, name: item.name ? item.name : item.label, translations: item.translations};
    const key = JSON.stringify(auxItem);
    this.filterAnalytics.delete(key);
  }

  getFilterAnalytics(){
    return Array.from(this.filterAnalytics).map(item => JSON.parse(item));
  }

  getItems() {
    return this.items;
  }

  setItems(items) {
    this.items = items;
  }

  /**
   * Funciones que se encarga de establecer el reseteo de los datos de una vista se comparte entre los componentes
   * Vistas exhibitors, novelties, products y filters
   * 
   */
  setResetList(active: boolean): void {
    this.resetItemsSubject.next(active);
  }

  getResetList(): Observable<boolean> {
    return this.resetItemsSubject;
  }

  /**
   * Funciones que se encarga de establecer los datos de una vista se comparte entre los componentes
   * Vistas exhibitors, novelties, products y filters
   * 
   */
  setItemsObs(newItems: any[]): void {
    this.itemsSubject.next(newItems);
  }
  getItemsObs(): Observable<any[]> {
    return this.items$;
  }

  /**
   * Funciones que se encarga de establecer el total de elementos de una vista se comparte entre los componentes
   * Vistas exhibitors, novelties, products y filters
   * 
   */
  setNumItemsObs(newItemsSize: number) {
    if(newItemsSize<=0)
      this.setResetList(true);
    this.numItemsSubject.next(newItemsSize);
  }

  getNumItemsObs(): Observable<number> {
    return this.numItems$;
  }

  saveSearchConfigInSession(config) {
    let eventData = JSON.parse(this.sessionService.get('eventData', false) || '{}');
    let nameStorageSearchConfig = `searchConfig_${eventData['sapCode']}`;    
    this.sessionService.set(nameStorageSearchConfig, config, false);
  }

  getCatalogueAndExhibitor(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.SEARCH.GET_CATALOGUE_EXHIBITOR + '?catalogue_id='+ dataIn.catalogueId +'&exhibitor_id=' + dataIn.exhibitorId;

    return this.http.get(url, dataIn);

  }

  getCatalogueAndProduct(dataIn) {

    let url = ENV.APP.BACKEND_API + CONSTANTS.END_POINTS.SEARCH.GET_CATALOGUE_PRODUCT + '?catalogue_id='+ dataIn.catalogueId +'&product_id=' + dataIn.productId;

    return this.http.get(url, dataIn);

  }

  resetFilters(){

    let restartFilter = this.searchConfig;

    restartFilter['searchText'] = '';
    restartFilter['selectedCountries'] = [];
    restartFilter['selectedHierarchicalProperties'] = [];
    restartFilter['selectedHomeOds'] = [];
    restartFilter['selectedMultiEvents'] = [];
    restartFilter['selectedProperties'] = [];
    restartFilter['selectedSectors'] = [];

    return restartFilter;
  }


}
